/** @graphql cc **/
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import gql from 'graphql-tag';
import { differenceInDays } from 'date-fns';

import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/error-types';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	CREATE_INTRO_FIRST_STEP_ONBOARDING_STATE_KEYS,
	ONBOARDING_EXPERIENCES,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useSessionData, AccessStatus } from '@confluence/session-data';

import type { useIsEligibleCreateIntroFirstStepUserPersonalizationQuery as useIsEligibleCreateIntroFirstStepUserPersonalizationQuery$data } from './__types__/useIsEligibleCreateIntroFirstStepUserPersonalizationQuery';
import type { useIsEligibleCreateIntroFirstStepGetPersonalSpaceKeyQuery as useIsEligibleCreateIntroFirstStepGetPersonalSpaceKeyQuery$data } from './__types__/useIsEligibleCreateIntroFirstStepGetPersonalSpaceKeyQuery';
import type {
	useIsEligibleCreateIntroFirstStepGetTemplateInfoQuery as useIsEligibleCreateIntroFirstStepGetTemplateInfoQuery$data,
	useIsEligibleCreateIntroFirstStepGetTemplateInfoQueryVariables as useIsEligibleCreateIntroFirstStepGetTemplateInfoQueryVariables$variables,
	useIsEligibleCreateIntroFirstStepGetTemplateInfoQuery_templateInfo_TemplateInfo as Template,
} from './__types__/useIsEligibleCreateIntroFirstStepGetTemplateInfoQuery';
import { INTRO_BLOG_BLUEPRINT_MODULE_COMPLETE_KEY } from './CreateIntroFirstStepConstants';

export const UseIsEligibleCreateIntroFirstStepUserPersonalizationQuery = gql`
	query useIsEligibleCreateIntroFirstStepUserPersonalizationQuery {
		license {
			licenseConsumingUserCount
		}
	}
`;

export const UseIsEligibleCreateIntroFirstStepGetPersonalSpaceKeyQuery = gql`
	query useIsEligibleCreateIntroFirstStepGetPersonalSpaceKeyQuery {
		user: confluence_atlassianUser(current: true) {
			id
			team
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

export const UseIsEligibleCreateIntroFirstStepGetTemplateInfoQuery = gql`
	query useIsEligibleCreateIntroFirstStepGetTemplateInfoQuery($blueprintKey: ID!) {
		templateInfo(id: $blueprintKey) {
			name
			templateType
			contentBlueprintId
			blueprintModuleCompleteKey
			itemModuleCompleteKey
		}
	}
`;

/**
 * Custom hook to determine eligibility for create intro first step experiment
 *
 * Conditions includes:
 * 1. Whether the user is an nth user
 * 2. Whether the user is on an instance with over ten users
 * 3. Whether the user is in the 14 day onboarding period
 * 4. Whether the user has already completed or dismissed the header
 * 5. Whether there is intro blog template data
 * 6. [Not checked in the code but in Statsig] Whether the user is a new user
 *
 * @returns {boolean} - Returns true if user passes all eligibility checks, otherwise false
 */
export const useIsEligibleCreateIntroFirstStep = () => {
	const { accessStatus, userId } = useSessionData();

	// User is licensed (site admin or nth user)
	const isLicensedUser =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	//  User is Nth user
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values({
			IS_FOUNDING_ADMIN: ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...CREATE_INTRO_FIRST_STEP_ONBOARDING_STATE_KEYS,
		}),
		!isLicensedUser || !userId || !accessStatus,
	);

	const { isFoundingAdmin, headerSeenAt, headerCompleted, headerDismissed } =
		deserializeState(onboardingStateData);
	const isNthUser = !isFoundingAdmin;

	//  Instance has >10 users
	const { data: userCountData, error: userCountDataError } =
		useQuery<useIsEligibleCreateIntroFirstStepUserPersonalizationQuery$data>(
			UseIsEligibleCreateIntroFirstStepUserPersonalizationQuery,
			{
				skip: !isLicensedUser || !isNthUser,
			},
		);

	const userCount = userCountData?.license?.licenseConsumingUserCount;
	const isOverTenUserInstance = userCount && userCount > 10;

	//  Check if user has personal space and get personal space key
	const { data: personalSpaceKeyData, error: personalSpaceKeyError } =
		useQuery<useIsEligibleCreateIntroFirstStepGetPersonalSpaceKeyQuery$data>(
			UseIsEligibleCreateIntroFirstStepGetPersonalSpaceKeyQuery,
			{
				skip: !userId || !isOverTenUserInstance || !isLicensedUser || !isNthUser,
			},
		);

	const hasPersonalSpace = personalSpaceKeyData?.user?.confluence?.hasPersonalSpace;
	const personalSpaceKey = hasPersonalSpace && personalSpaceKeyData?.user?.confluence?.space?.key;

	// Must  be in 14 day onboarding period
	const underFourteenDays =
		Boolean(headerSeenAt) && differenceInDays(new Date(), new Date(headerSeenAt)) <= 14;
	const isInOnboardingPeriod = underFourteenDays || headerSeenAt === undefined;

	//  Get intro blog template info, if this fails do not show experiment
	const {
		data: templateData,
		loading: templateLoading,
		error: templateError,
	} = useQuery<
		useIsEligibleCreateIntroFirstStepGetTemplateInfoQuery$data,
		useIsEligibleCreateIntroFirstStepGetTemplateInfoQueryVariables$variables
	>(UseIsEligibleCreateIntroFirstStepGetTemplateInfoQuery, {
		variables: {
			blueprintKey: INTRO_BLOG_BLUEPRINT_MODULE_COMPLETE_KEY,
		},
		skip: !isLicensedUser || !isNthUser || !isOverTenUserInstance || !isInOnboardingPeriod,
	});

	const introBlogTemplate = templateData?.templateInfo as Template | null;

	const eligibilityError =
		onboardingStateError || userCountDataError || personalSpaceKeyError || templateError;

	if (
		(eligibilityError && isUnauthorizedError(eligibilityError)) ||
		isExpectedApolloError(eligibilityError)
	) {
		markErrorAsHandled(eligibilityError);
	}

	//  Eligibility for experiment, meets requirements for enrollment check
	const isEligible =
		!eligibilityError &&
		isInOnboardingPeriod &&
		isLicensedUser &&
		isNthUser &&
		isOverTenUserInstance &&
		!!introBlogTemplate &&
		!!personalSpaceKey;

	//  Whether to show the experiment component
	const isVisible = isEligible && !headerDismissed && !headerCompleted;

	return {
		isEligibleCreateIntroFirstStep: isEligible,
		isVisibleCreateIntroFirstStep: isVisible,
		personalSpaceKey,
		headerSeenAt,
		introBlogTemplate,
		loading: onboardingStateLoading || templateLoading,
		error: eligibilityError,
	};
};
